import styled from '@emotion/styled';
import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { Currency, DonationPlan } from '../../../types/PaymentTypes';
import { microcopySet, useMicrocopy } from '../../../utils/contentfulMicrocopy';
import { formatAmountAndWithDerivedLocale } from '../../../utils/helpers/currency';
import { colors, spacing } from '../../../utils/styleguide';
import AmountInput from '../forms/AmountInput';
import Typography from '../text/Typography';
import DonateAmount from './DonateAmount';
import CurrencyDropDown from '../forms/CurrencyDropDown';

const Amounts = styled.div`
  display: flex;
  align-items: stretch;
  > label {
    margin-right: ${spacing[1]}px;
  }
  > label:last-child {
    margin-right: 0;
  }
  margin-bottom: ${spacing[2]}px;
`;

const CustomAmountInput = styled(AmountInput)`
  flex: 1;
  margin-bottom: ${spacing[2]}px;
`;

const AmountSelectorForm = styled.div`
  display: flex;
  flex-direction: column;
`;
const CustomAmountSkeleton = styled.div`
  min-height: 58px;
  margin-top: 8px;
  margin-bottom: ${spacing[2]}px;
  width: 100%;
  border: 1px solid;
  border-color: ${colors.grey};
`;

type Props = {
  amountCustomLabels?: ReactNode[];
  amountOptions?: number[];
  customAmountLabel?: string | null;
  customAmountError?: string;
  amount?: number;
  isCustomAmount?: boolean;
  onAmountChange: (params: { amount: number; isCustomAmount: boolean; isValid: boolean }) => void;
  currency?: Currency;
  disabled?: boolean;
  className?: string;
  showSkeleton?: boolean;
  type?: DonationPlan;
  colorTheme?: 'default' | 'dark' | 'ppc';
  maximumAmount?: number;
  currencies?: { code: string; name: string }[];
  handleSelectCurrency?: (currencyCode: string) => void;
  isLoadingCurrencies?: boolean;
};

const DEFAULT_MINIMUM_AMOUNT = 0;
const DEFAULT_MAXIMUM_AMOUNT = 1_000_000;

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  gap: ${spacing[1]}px;
`;

const AmountSelector = ({
  amountCustomLabels,
  amount,
  amountOptions,
  className,
  currency,
  customAmountError,
  customAmountLabel,
  disabled,
  isCustomAmount,
  onAmountChange,
  showSkeleton,
  colorTheme,
  type = 'one time',
  maximumAmount,
  currencies,
  handleSelectCurrency,
  isLoadingCurrencies,
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<undefined | string>();
  const t = useMicrocopy();
  const minimumAmount = amountOptions?.[0];
  const minimumAmountError = `${t(
    microcopySet.PAYMENT_OPTION,
    'minimumCustomAmount',
  )} ${formatAmountAndWithDerivedLocale(
    minimumAmount ?? DEFAULT_MINIMUM_AMOUNT,
    currency?.code ?? 'SEK',
  )}.`;
  const maximumAmountError = `${t(
    microcopySet.PAYMENT_OPTION,
    'maximumCustomAmount',
  )} ${formatAmountAndWithDerivedLocale(
    maximumAmount ?? DEFAULT_MAXIMUM_AMOUNT,
    currency?.code ?? 'SEK',
  )}.`;

  // FIXME: isValid logic should be moved to parent components
  // and duplicate code in useEffect should be removed
  function handleCustomAmountChange(event: ChangeEvent<HTMLInputElement>) {
    const amount = parseInt(event.target.value);
    const isAboveMinimum = amount >= (minimumAmount ?? 0);
    const isBelowMaximum = amount <= (maximumAmount ?? Infinity);
    onAmountChange({ amount, isCustomAmount: true, isValid: isAboveMinimum && isBelowMaximum });
    setErrorMessage(isAboveMinimum ? undefined : minimumAmountError);
  }

  useEffect(() => {
    if (amount && minimumAmount) {
      let errorMessage = undefined;
      const isAboveMinimum = amount >= (minimumAmount ?? DEFAULT_MINIMUM_AMOUNT);
      const isBelowMaximum = amount <= (maximumAmount ?? DEFAULT_MAXIMUM_AMOUNT);
      if (!isAboveMinimum) {
        errorMessage = minimumAmountError;
      } else if (!isBelowMaximum) {
        errorMessage = maximumAmountError;
      }

      setErrorMessage(errorMessage);
    }
  }, [amount, minimumAmount, minimumAmountError, maximumAmount, maximumAmountError]);

  if (showSkeleton) {
    return (
      <AmountSelectorForm className={className}>
        <Amounts>
          <DonateAmount showSkeleton={true} isSelected={false} onSelectAmount={() => {}} />
          <DonateAmount showSkeleton={true} isSelected={false} onSelectAmount={() => {}} />
          <DonateAmount showSkeleton={true} isSelected={false} onSelectAmount={() => {}} />
        </Amounts>
        {customAmountLabel && <CustomAmountSkeleton />}
      </AmountSelectorForm>
    );
  }

  return (
    <AmountSelectorForm>
      <Amounts role="radiogroup">
        {currency &&
          amountOptions?.map((amountOption, i) => (
            <DonateAmount
              key={i}
              isSelected={!isCustomAmount && amountOption === amount}
              onSelectAmount={() => {
                onAmountChange({ amount: amountOption, isCustomAmount: false, isValid: true });
              }}
              currency={currency}
              amount={amountOption}
              type={type}
              disabled={disabled}
              showSkeleton={amountOption === 0 || !currency}
              colorTheme={colorTheme}
              amountCustomLabel={amountCustomLabels?.[i]}
            />
          ))}
      </Amounts>
      {customAmountLabel && currency && minimumAmount && (
        <>
          <Wrapper>
            <CustomAmountInput
              amount={isCustomAmount ? amount : undefined}
              currency={currency}
              errorMessage={isCustomAmount ? errorMessage : undefined}
              isMonthlyPayment={type === 'monthly'}
              onChange={handleCustomAmountChange}
              onFocus={handleCustomAmountChange}
              placeholder={customAmountLabel}
            />

            {currencies && (
              <CurrencyDropDown
                selectedCurrency={currency?.code}
                currencies={currencies}
                onCurrencySelect={handleSelectCurrency}
                showSkeleton={isLoadingCurrencies}
              />
            )}
          </Wrapper>

          <Typography
            id="select-amount-error-message"
            useBuiltInMargins
            variant="detail"
            tag="span"
            color={colors.red}
          >
            {customAmountError}
          </Typography>
        </>
      )}
    </AmountSelectorForm>
  );
};

export default AmountSelector;
